<template>
  <div class="course-progress-page">
    <div v-if="loading" class="status-card">正在加载学习进度...</div>
    <div v-else-if="errorMessage" class="status-card is-error">
      {{ errorMessage }}
    </div>
    <template v-else>
      <!-- 进度概览卡片 -->
      <section class="progress-overview panel">
        <h2 class="section-title">学习进度概览</h2>
        <div class="overview-grid">
          <div class="overview-card">
            <div class="overview-card__icon">📚</div>
            <div class="overview-card__content">
              <span class="overview-card__label">课程名称</span>
              <strong class="overview-card__value">{{ overview.course_title }}</strong>
            </div>
          </div>

          <div class="overview-card">
            <div class="overview-card__icon">✅</div>
            <div class="overview-card__content">
              <span class="overview-card__label">完成进度</span>
              <strong class="overview-card__value">
                {{ overview.completed_lessons }} / {{ overview.total_lessons }}
              </strong>
              <span class="overview-card__sub">{{ overview.progress_percent }}%</span>
            </div>
          </div>

          <div class="overview-card">
            <div class="overview-card__icon">⏱️</div>
            <div class="overview-card__content">
              <span class="overview-card__label">学习时长</span>
              <strong class="overview-card__value">{{ formatDuration(overview.total_study_duration) }}</strong>
            </div>
          </div>

          <div class="overview-card">
            <div class="overview-card__icon">🎯</div>
            <div class="overview-card__content">
              <span class="overview-card__label">考试通过</span>
              <strong class="overview-card__value">
                {{ overview.passed_exams }} / {{ overview.total_exams }}
              </strong>
            </div>
          </div>
        </div>

        <!-- 进度条 -->
        <div class="progress-bar-wrapper">
          <div class="progress-bar">
            <div
              class="progress-bar__fill"
              :style="{ width: overview.progress_percent + '%' }"
            ></div>
          </div>
          <span class="progress-bar__text">{{ overview.progress_percent }}% 完成</span>
        </div>
      </section>

      <!-- 章节进度列表 -->
      <section class="chapter-progress panel">
        <h2 class="section-title">章节学习进度</h2>
        <div class="chapter-list">
          <div
            v-for="chapter in chapterList"
            :key="chapter.id"
            class="chapter-item"
          >
            <div class="chapter-header" @click="toggleChapter(chapter.id)">
              <div class="chapter-info">
                <span class="chapter-icon">📖</span>
                <h3 class="chapter-title">{{ chapter.title }}</h3>
                <span class="chapter-progress">
                  {{ chapter.lesson_completed }} / {{ chapter.lesson_total }} 课时
                </span>
              </div>
              <div class="chapter-status">
                <div class="mini-progress">
                  <div
                    class="mini-progress__fill"
                    :style="{ width: chapter.progress_percent + '%' }"
                  ></div>
                </div>
                <span class="chapter-percent">{{ chapter.progress_percent }}%</span>
                <span
                  v-if="chapter.exam_passed"
                  class="exam-badge exam-badge--passed"
                >
                  ✓ 考试已通过
                </span>
                <span
                  class="toggle-icon"
                  :class="{ 'is-open': expandedChapters.includes(chapter.id) }"
                >
                  ▼
                </span>
              </div>
            </div>

            <!-- 小节列表 -->
            <div
              v-if="expandedChapters.includes(chapter.id) && chapter.children"
              class="section-list"
            >
              <div
                v-for="section in chapter.children"
                :key="section.id"
                class="section-item"
              >
                <div class="section-info">
                  <span class="section-icon">📄</span>
                  <h4 class="section-title">{{ section.title }}</h4>
                  <span class="section-progress">
                    {{ section.lesson_completed }} / {{ section.lesson_total }} 课时
                  </span>
                </div>
                <div class="section-status">
                  <div class="mini-progress">
                    <div
                      class="mini-progress__fill"
                      :style="{ width: section.progress_percent + '%' }"
                    ></div>
                  </div>
                  <span class="section-percent">{{ section.progress_percent }}%</span>
                  <span
                    v-if="section.exam_passed"
                    class="exam-badge exam-badge--passed"
                  >
                    ✓ 考试已通过
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { fetchCourseProgressOverview, fetchCourseProgressChapter } from '@/api/courseProgress'

export default {
  name: 'CourseProgress',
  props: {
    courseId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      overview: {
        course_title: '',
        total_lessons: 0,
        completed_lessons: 0,
        progress_percent: 0,
        total_study_duration: 0,
        total_exams: 0,
        passed_exams: 0
      },
      chapterList: [],
      expandedChapters: []
    }
  },
  mounted() {
    this.loadProgressData()
  },
  methods: {
    async loadProgressData() {
      this.loading = true
      this.errorMessage = ''

      try {
        // 并行加载概览和章节进度
        const [overviewData, chapterData] = await Promise.all([
          fetchCourseProgressOverview(this.courseId),
          fetchCourseProgressChapter(this.courseId)
        ])

        this.overview = overviewData
        this.chapterList = chapterData.chapter_list || []

        // 默认展开第一个章节
        if (this.chapterList.length > 0) {
          this.expandedChapters = [this.chapterList[0].id]
        }
      } catch (error) {
        console.error('加载学习进度失败:', error)
        this.errorMessage = error.message || '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    toggleChapter(chapterId) {
      const index = this.expandedChapters.indexOf(chapterId)
      if (index > -1) {
        this.expandedChapters.splice(index, 1)
      } else {
        this.expandedChapters.push(chapterId)
      }
    },
    formatDuration(seconds) {
      if (!seconds) return '0分钟'

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)

      if (hours > 0) {
        return `${hours}小时${minutes}分钟`
      }
      return `${minutes}分钟`
    }
  }
}
</script>

<style scoped>
.course-progress-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.status-card {
  padding: 40px;
  text-align: center;
  background: #f5f7fa;
  border-radius: 8px;
  color: #606266;
}

.status-card.is-error {
  background: #fef0f0;
  color: #f56c6c;
}

.panel {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #303133;
}

/* 概览卡片 */
.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.overview-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
  gap: 16px;
}

.overview-card__icon {
  font-size: 32px;
}

.overview-card__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.overview-card__label {
  font-size: 12px;
  color: #909399;
}

.overview-card__value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.overview-card__sub {
  font-size: 14px;
  color: #67c23a;
  font-weight: 500;
}

/* 进度条 */
.progress-bar-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  flex: 1;
  height: 24px;
  background: #e4e7ed;
  border-radius: 12px;
  overflow: hidden;
}

.progress-bar__fill {
  height: 100%;
  background: linear-gradient(90deg, #409eff, #67c23a);
  transition: width 0.3s ease;
}

.progress-bar__text {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  min-width: 80px;
  text-align: right;
}

/* 章节列表 */
.chapter-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chapter-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
}

.chapter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.chapter-header:hover {
  background: #f5f7fa;
}

.chapter-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.chapter-icon {
  font-size: 20px;
}

.chapter-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #303133;
}

.chapter-progress {
  font-size: 14px;
  color: #909399;
}

.chapter-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mini-progress {
  width: 100px;
  height: 8px;
  background: #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.mini-progress__fill {
  height: 100%;
  background: #409eff;
  transition: width 0.3s ease;
}

.chapter-percent,
.section-percent {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  min-width: 45px;
}

.exam-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.exam-badge--passed {
  background: #f0f9ff;
  color: #67c23a;
}

.toggle-icon {
  font-size: 12px;
  color: #909399;
  transition: transform 0.3s;
}

.toggle-icon.is-open {
  transform: rotate(180deg);
}

/* 小节列表 */
.section-list {
  background: #fafafa;
  padding: 8px 16px;
}

.section-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 8px;
}

.section-item:last-child {
  margin-bottom: 0;
}

.section-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.section-icon {
  font-size: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: #606266;
}

.section-progress {
  font-size: 12px;
  color: #909399;
}

.section-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 768px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }

  .chapter-header,
  .section-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .chapter-status,
  .section-status {
    width: 100%;
  }
}
</style>
